import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import { Bot } from "lucide-react";
import StopIcon from "@mui/icons-material/Stop";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React from "react";
import RswEditor, { EditorProvider } from "react-simple-wysiwyg";
import AgentNotificationSettings from "../../../components/AgentNotificationSettings";
import CrmIntegrationSelect from "../../../components/CrmIntegrationSelect";
import KnowledgeBaseSelect from "../../../components/KnowledgeBaseSelect";
import LoadingButton from "../../../components/LoadingButton";
import MCPServerSelect from "../../../components/MCPServerSelect";
import ModelSelector from "../../../components/ModelSelector";
import PopOutTextInput from "../../../components/PopOutTextInput";
import SearchableIntegrationSelect from "../../../components/SearchableIntegrationSelect";
import SearchableSenderProfileSelect, {
  loadAllSenderProfiles,
} from "../../../components/SearchableSenderProfileSelect";
import ChannelSchedule from "../../../components/ChannelSchedule";
import ToolsetSelect from "../../../components/ToolsetSelect";
import ToolsetsManagementDialog from "../../integrations/dialogs/ToolsetsManagementDialog";
import SenderProfiles from "../../sequences/SenderProfiles";
import GenerateWidgetGifDialog from "./GenerateWidgetGifDialog";
import HandoffSection from "../components/HandoffSection";
import SmartListPicker from "../../../components/SmartListPicker";
import {
  getAvailableLeadFields,
  useDefaultFields,
} from "../../../hooks/useDefaultFields";
import { hostname, isLocal, twilioWebhookHostname } from "../../../utils";
import { useEmployeeTerminology } from "../../../utils/terminology";
import { fetchIntegrationsByType } from "../../integrations/api";

interface AgentFormDialogProps {
  open: boolean;
  onClose: () => void;
  editingAgent: any;
  agentName: string;
  setAgentName: (name: string) => void;
  avatarImage: string;
  setAvatarImage: (image: string) => void;
  avatarUploadLoading: boolean;
  handleAvatarUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  systemPrompt: string;
  setSystemPrompt: (prompt: string) => void;
  systemPromptTemplate: string;
  handleTemplateChange: (templateId: string) => void;
  systemPromptTemplates: Array<{ id: string; name: string; prompt: string }>;
  selectedEmployee: any;
  setEmployeeSelectOpen: (open: boolean) => void;
  model: string;
  setModel: (model: string) => void;
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  secondaryColor: string;
  setSecondaryColor: (color: string) => void;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  knowledgeBaseEnabled: boolean;
  setKnowledgeBaseEnabled: (enabled: boolean) => void;
  knowledgeBaseScope: string;
  setKnowledgeBaseScope: (scope: string) => void;
  channelSettings: any;
  updateWebsiteSettings: (updates: any) => void;
  updateSmsSettings: (updates: any) => void;
  updateEmailSettings: (updates: any) => void;
  updateVoiceSettings: (updates: any) => void;
  updateWebpageSettings: (updates: any) => void;
  positions: Array<{ value: string; label: string }>;
  leadFields: Array<any>;
  setLeadFields: (fields: Array<any>) => void;
  contactListId: string;
  setContactListId: (listId: string) => void;
  debounceTimeout: number;
  setDebounceTimeout: (timeout: number) => void;
  notificationSettings: any;
  setNotificationSettings: (settings: any) => void;
  mcpServers: string[];
  setMcpServers: (servers: string[]) => void;
  toolsets: string[];
  setToolsets: (toolsets: string[]) => void;
  crmSyncEnabled: boolean;
  setCrmSyncEnabled: (enabled: boolean) => void;
  crmIntegrationId: string;
  setCrmIntegrationId: (id: string) => void;
  schedulingLink: string;
  setSchedulingLink: (link: string) => void;
  schedulingLinkText: string;
  setSchedulingLinkText: (text: string) => void;
  handoffTargets: Array<{
    id: string;
    name: string;
    description: string;
    email: string;
    phone: string;
  }>;
  setHandoffTargets: (targets: Array<{
    id: string;
    name: string;
    description: string;
    email: string;
    phone: string;
  }>) => void;
  onSave: () => void;
  saveLoading: boolean;
  currentPlan: string;
  companyId: string;
  company: any;
  getToken: Function;
  eventListener: Function;
  allAgents?: Array<any>;
  employees?: Array<any>;
}

// CSS for spinning animation
const spinAnimation = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const AgentFormDialog: React.FC<AgentFormDialogProps> = ({
  open,
  onClose,
  editingAgent,
  agentName,
  setAgentName,
  avatarImage,
  setAvatarImage,
  avatarUploadLoading,
  handleAvatarUpload,
  systemPrompt,
  setSystemPrompt,
  systemPromptTemplate,
  handleTemplateChange,
  systemPromptTemplates,
  selectedEmployee,
  setEmployeeSelectOpen,
  model,
  setModel,
  primaryColor,
  setPrimaryColor,
  secondaryColor,
  setSecondaryColor,
  isActive,
  setIsActive,
  knowledgeBaseEnabled,
  setKnowledgeBaseEnabled,
  knowledgeBaseScope,
  setKnowledgeBaseScope,
  channelSettings,
  updateWebsiteSettings,
  updateSmsSettings,
  updateEmailSettings,
  updateVoiceSettings,
  updateWebpageSettings,
  positions,
  leadFields,
  setLeadFields,
  contactListId,
  setContactListId,
  debounceTimeout,
  setDebounceTimeout,
  notificationSettings,
  setNotificationSettings,
  mcpServers,
  setMcpServers,
  toolsets,
  setToolsets,
  crmSyncEnabled,
  setCrmSyncEnabled,
  crmIntegrationId,
  setCrmIntegrationId,
  schedulingLink,
  setSchedulingLink,
  schedulingLinkText,
  setSchedulingLinkText,
  handoffTargets,
  setHandoffTargets,
  onSave,
  saveLoading,
  currentPlan,
  companyId,
  company,
  getToken,
  eventListener,
  allAgents,
  employees = [],
}) => {
  const { employee: employeeText, employeeLower } = useEmployeeTerminology();
  const [twilioIntegrations, setTwilioIntegrations] = React.useState<
    Array<any>
  >([]);
  const [twilioLoading, setTwilioLoading] = React.useState(false);
  const [phoneNumbers, setPhoneNumbers] = React.useState<Array<any>>([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = React.useState(false);
  const [webhookConfiguring, setWebhookConfiguring] = React.useState(false);
  const [emailIntegrations, setEmailIntegrations] = React.useState<Array<any>>(
    []
  );
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [senderProfiles, setSenderProfiles] = React.useState<Array<any>>([]);
  const [senderProfilesLoading, setSenderProfilesLoading] =
    React.useState(false);
  const [senderProfilesDialogOpen, setSenderProfilesDialogOpen] =
    React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);

  // Toolsets management dialog state
  const [toolsetsDialogOpen, setToolsetsDialogOpen] = React.useState(false);
  const [toolsetSelectKey, setToolsetSelectKey] = React.useState(0);

  // Generate Widget GIF dialog state
  const [generateGifDialogOpen, setGenerateGifDialogOpen] =
    React.useState(false);

  // ElevenLabs voice states
  const [elevenLabsVoices, setElevenLabsVoices] = React.useState<Array<any>>(
    []
  );
  const [voicesLoading, setVoicesLoading] = React.useState(false);
  const [playingVoiceId, setPlayingVoiceId] = React.useState<string | null>(
    null
  );
  const [voicePreviewLoading, setVoicePreviewLoading] = React.useState<
    string | null
  >(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Load ElevenLabs voices
  React.useEffect(() => {
    if (open) {
      setVoicesLoading(true);
      getToken().then((token: string) => {
        axios
          .get(`${hostname}/voices`, {
            params: { companyId, includePublic: true, pageSize: 1000 },
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => {
            if (res.data.items) {
              setElevenLabsVoices(res.data.items);
            }
          })
          .catch((err) => {
            console.error("Failed to load ElevenLabs voices:", err);
            // Use fallback voices if API fails
            setElevenLabsVoices([
              {
                voice_id: "pNInz6obpgDQGcFmaJgB",
                name: "Adam",
                category: "Male, Conversational",
              },
              {
                voice_id: "EXAVITQu4vr4xnSDxMaL",
                name: "Bella",
                category: "Female, Warm",
              },
              {
                voice_id: "ErXwobaYiN019PkySvjV",
                name: "Antoni",
                category: "Male, Professional",
              },
              {
                voice_id: "MF3mGyEYCl7XYWbV9V6O",
                name: "Elli",
                category: "Female, Energetic",
              },
              {
                voice_id: "TxGEqnHWrfWFTfGW9XjX",
                name: "Josh",
                category: "Male, Calm",
              },
              {
                voice_id: "JBFqnCBsd6RMkjVDRZzb",
                name: "Rachel",
                category: "Female, Professional",
              },
              {
                voice_id: "yoZ06aMxZJJ28mfd3POQ",
                name: "Sam",
                category: "Male, Young",
              },
            ]);
          })
          .finally(() => {
            setVoicesLoading(false);
          });
      });
    }
  }, [open, getToken, eventListener]);

  // Function to preview voice
  const handleVoicePreview = async (
    voiceId: string,
    voiceName: string,
    event?: React.MouseEvent
  ) => {
    // Prevent the autocomplete from selecting this option when clicking play
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // If already playing this voice, stop it
    if (playingVoiceId === voiceId && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingVoiceId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingVoiceId(null);
    }

    try {
      setVoicePreviewLoading(voiceId);
      const token = await getToken();

      // Generate preview text
      const previewText = `Hello! This is ${voiceName}. I'm here to help you with any questions or tasks you might have.`;

      // Call backend to generate audio
      const response = await axios.post(
        `${hostname}/voice-ws/elevenlabs/preview`,
        {
          voiceId: voiceId,
          voiceName: voiceName,
          ttsModel: channelSettings.voice?.ttsModel || "eleven_flash_v2_5",
          text: previewText,
          companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      // Create audio URL and play
      const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingVoiceId(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audio.onerror = () => {
        console.error("Error playing audio");
        setPlayingVoiceId(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      await audio.play();
      setPlayingVoiceId(voiceId);
    } catch (error) {
      console.error("Failed to preview voice:", error);
      eventListener({
        type: "SET_ERROR",
        error: "Failed to preview voice. Please try again.",
      });
    } finally {
      setVoicePreviewLoading(null);
    }
  };

  // Cleanup audio on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Load Twilio integrations for SMS
  React.useEffect(() => {
    if (open) {
      setTwilioLoading(true);
      fetchIntegrationsByType(companyId, "sms", getToken)
        .then((integrations) => {
          setTwilioIntegrations(integrations || []);
        })
        .catch((err) => {
          console.log("No Twilio integrations available:", err);
          setTwilioIntegrations([]);
        })
        .finally(() => {
          setTwilioLoading(false);
        });
    }
  }, [open, companyId, getToken]);

  // Load email integrations for Email
  React.useEffect(() => {
    if (open) {
      setEmailLoading(true);
      fetchIntegrationsByType(companyId, "email", getToken)
        .then((integrations) => {
          setEmailIntegrations(integrations || []);
        })
        .catch((err) => {
          console.error("Failed to load email integrations:", err);
          setEmailIntegrations([]);
        })
        .finally(() => {
          setEmailLoading(false);
        });
    }
  }, [open, companyId, getToken]);

  // Load sender profiles for Email
  React.useEffect(() => {
    if (open) {
      setSenderProfilesLoading(true);
      getToken().then(async (token: string) => {
        try {
          const allProfiles = await loadAllSenderProfiles(token, companyId);
          setSenderProfiles(allProfiles);
        } catch (err) {
          console.error("Failed to load sender profiles:", err);
          setSenderProfiles([]);
        } finally {
          setSenderProfilesLoading(false);
        }
      });
    }
  }, [open, companyId, getToken]);

  // Auto-populate email address and integration when sender profile is selected
  React.useEffect(() => {
    const selectedProfileId = channelSettings.email?.senderProfileId;
    const currentEmailAddress = channelSettings.email?.emailAddress;
    const currentIntegrationId = channelSettings.email?.integrationId;

    if (selectedProfileId && senderProfiles.length > 0) {
      const selectedProfile = senderProfiles.find(
        (profile) => profile.id === selectedProfileId
      );
      if (selectedProfile) {
        const updates: any = {};

        // Auto-set email address from sender profile
        if (selectedProfile.email !== currentEmailAddress) {
          updates.emailAddress = selectedProfile.email;
        }

        // Auto-set integration from sender profile
        if (selectedProfile.integrationId && selectedProfile.integrationId !== currentIntegrationId) {
          updates.integrationId = selectedProfile.integrationId;
        }

        if (Object.keys(updates).length > 0) {
          updateEmailSettings(updates);
        }
      }
    }
    // Note: We don't clear the email address when no profile is selected to avoid loops
    // The email field is always disabled, so users can't edit it anyway
  }, [
    channelSettings.email?.senderProfileId,
    channelSettings.email?.emailAddress,
    channelSettings.email?.integrationId,
    senderProfiles,
    updateEmailSettings,
  ]);

  // Load phone numbers when Twilio integration is selected
  const loadPhoneNumbers = React.useCallback(
    async (integrationId: string) => {
      if (!integrationId) {
        setPhoneNumbers([]);
        return;
      }

      setPhoneNumbersLoading(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/twilio/${integrationId}/phone-numbers`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setPhoneNumbers(response.data.phoneNumbers || []);
      } catch (err) {
        console.log("Could not load phone numbers:", err);
        setPhoneNumbers([]);
        // Don't show error popup - this is expected when Twilio isn't configured
      } finally {
        setPhoneNumbersLoading(false);
      }
    },
    [companyId, getToken, eventListener]
  );

  // Helper function to check which agent is using a phone number
  const getAgentUsingPhoneNumber = (
    phoneNumber: string,
    channel: "sms" | "voice"
  ) => {
    if (!allAgents || !phoneNumber) return null;

    return allAgents.find((agent) => {
      if (agent.id === editingAgent?.id) return false; // Exclude current agent
      const settings = agent.channelSettings?.[channel];
      return (
        settings?.enabled &&
        settings?.phoneNumber === phoneNumber &&
        agent.isActive
      );
    });
  };

  // Configure webhook when phone number is selected
  const configureWebhook = React.useCallback(
    async (phoneNumber: string, integrationId: string) => {
      if (!phoneNumber || !integrationId) return;

      const selectedPhone = phoneNumbers.find(
        (p) => p.phoneNumber === phoneNumber
      );
      if (!selectedPhone) return;

      setWebhookConfiguring(true);
      try {
        const token = await getToken();
        // Use current domain for webhook URL to support whitelabel
        const currentDomain = isLocal
          ? twilioWebhookHostname
          : window.location.origin;
        const webhookUrl = `${currentDomain}/webhooks/twilio/sms`;

        const response = await axios.post(
          `${hostname}/integrations/twilio/${integrationId}/configure-webhook`,
          {
            companyId,
            phoneNumberSid: selectedPhone.sid,
            webhookUrl,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          eventListener({
            type: "SET_SUCCESS",
            message:
              "Webhook configured successfully. SMS messages will now be routed to your agent.",
          });
        }
      } catch (err) {
        console.error("Failed to configure webhook:", err);
        eventListener({
          type: "SET_ERROR",
          error:
            "Failed to configure webhook. You may need to set it up manually in Twilio console.",
        });
      } finally {
        setWebhookConfiguring(false);
      }
    },
    [phoneNumbers, companyId, getToken, eventListener]
  );

  // Handle voice configuration save
  const handleVoiceConfigSave = React.useCallback(
    async (elevenLabsVoiceId?: string) => {
      if (!editingAgent?.id) return;

      try {
        const token = await getToken();

        const requestData: any = {
          companyId,
        };

        if (elevenLabsVoiceId) {
          requestData.elevenLabsVoiceId = elevenLabsVoiceId;
        }

        const response = await axios.post(
          `${hostname}/voice-ws/agents/${editingAgent.id}/configure-voice`,
          requestData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          console.log("Voice configuration saved successfully:", response.data);
        }
      } catch (err: any) {
        console.error("Failed to save voice configuration:", err);

        let errorMessage = "Failed to save voice configuration. ";

        if (err.response?.data?.error) {
          errorMessage += err.response.data.error;
        }

        eventListener({
          type: "SET_ERROR",
          error: errorMessage,
        });
      }
    },
    [editingAgent?.id, companyId, getToken, eventListener]
  );

  // Configure voice webhook when phone number is selected
  const configureVoiceWebhook = React.useCallback(
    async (phoneNumber: string, integrationId: string) => {
      if (!phoneNumber || !integrationId) return;

      const selectedPhone = phoneNumbers.find(
        (p) => p.phoneNumber === phoneNumber
      );
      if (!selectedPhone) return;

      setWebhookConfiguring(true);
      try {
        const token = await getToken();
        // Voice webhook URL - Use current domain for whitelabel support
        const currentDomain = isLocal
          ? twilioWebhookHostname
          : window.location.origin;
        const webhookUrl = `${currentDomain}/voice-ws/twilio/voice/websocket/${editingAgent?.id}`;

        const response = await axios.post(
          `${hostname}/voice-ws/agents/${editingAgent?.id}/update-twilio-webhook`,
          {
            companyId,
            phoneNumberSid: selectedPhone.sid,
            webhookUrl: webhookUrl,
            integrationId: integrationId, // Pass the integration ID
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          // Also save voice configuration
          await handleVoiceConfigSave(channelSettings.voice?.elevenLabsVoiceId);

          eventListener({
            type: "SET_SUCCESS",
            message:
              "Voice webhook configured successfully. Voice calls will now be routed to your agent.",
          });
        }
      } catch (err: any) {
        console.error("Failed to configure voice webhook:", err);

        let errorMessage = "Failed to configure voice webhook. ";

        if (err.response?.status === 403) {
          errorMessage +=
            "You don't have permission to update this phone number. Please check your Twilio credentials.";
        } else if (err.response?.status === 404) {
          errorMessage +=
            "The phone number or integration was not found. Please refresh and try again.";
        } else if (err.response?.data?.error) {
          errorMessage += err.response.data.error;
        } else {
          errorMessage +=
            "You may need to set it up manually in Twilio console.";
        }

        eventListener({
          type: "SET_ERROR",
          error: errorMessage,
        });
      } finally {
        setWebhookConfiguring(false);
      }
    },
    [
      phoneNumbers,
      companyId,
      getToken,
      eventListener,
      editingAgent?.id,
      handleVoiceConfigSave,
      channelSettings.voice?.elevenLabsVoiceId,
    ]
  );

  // Load phone numbers when integration changes
  React.useEffect(() => {
    const smsIntegrationId = channelSettings.sms?.integrationId;
    const voiceIntegrationId = channelSettings.voice?.integrationId;

    // Use the first available integration ID
    const integrationId = smsIntegrationId || voiceIntegrationId;

    if (integrationId && open) {
      loadPhoneNumbers(integrationId);
    } else {
      setPhoneNumbers([]);
    }
  }, [
    channelSettings.sms?.integrationId,
    channelSettings.voice?.integrationId,
    open,
  ]); // Removed loadPhoneNumbers to prevent infinite loop

  // Get default fields from backend
  const { defaultFields, loading: fieldsLoading } = useDefaultFields("leads");

  // Get available lead fields from backend data
  const availableFields = React.useMemo(() => {
    return getAvailableLeadFields(defaultFields);
  }, [defaultFields]);

  const handleLeadFieldToggle = (fieldKey: string) => {
    const existingField = leadFields.find((f) => f.key === fieldKey);
    if (existingField) {
      setLeadFields(leadFields.filter((f) => f.key !== fieldKey));
    } else {
      const field = availableFields.find((f) => f.key === fieldKey);
      if (field) {
        const newField = {
          key: fieldKey,
          label: field.label,
          type: field.type === "string" ? "text" : field.type,
          required: ["firstName", "lastName", "email"].includes(fieldKey),
        };
        setLeadFields([...leadFields, newField]);
      }
    }
  };

  return (
    <>
      <style>{spinAnimation}</style>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        sx={{
          "& .MuiDialog-paper": {
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>
          {editingAgent ? "Edit Agent" : "Create Agent"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ overflow: "hidden", display: "flex", flexDirection: "column" }}
        >
          <DialogContentText>
            Configure your agent settings and appearance
          </DialogContentText>

          <Box
            sx={{
              width: "100%",
              mt: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ "& .MuiTab-root": { mx: 1 } }}
              >
                <Tab label="General" />
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      Integrations
                      <Chip label="Beta" size="small" color="primary" />
                    </Box>
                  }
                />
                <Tab label="Widget" />
                <Tab label="Webpage" />
                <Tab label="SMS" />
                <Tab label="Voice" />
                <Tab label="Email" />
                <Tab label="Handoff" />
                <Tab label="Notifications" />
              </Tabs>
            </Box>

            {/* Tab Content Container */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                pt: 2,
                px: 1,
              }}
            >
              {/* Basic Settings Tab */}
              {currentTab === 0 && (
                <Box>
                  {/* Agent Status Toggle */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 3,
                      p: 2,
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Agent Status
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isActive
                          ? "This agent is live and responding to messages"
                          : "This agent is paused and will not respond"}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={isActive ? "Active" : "Inactive"}
                      labelPlacement="start"
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontWeight: "medium",
                          mr: 1,
                        },
                      }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Agent Name"
                        value={agentName}
                        onChange={(e) => setAgentName(e.target.value)}
                        fullWidth
                        required
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ModelSelector
                        currentPlan={currentPlan}
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        tools={true}
                        sx={{ mt: 0 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Agent Avatar
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            src={avatarImage}
                            sx={{
                              width: 64,
                              height: 64,
                              bgcolor: primaryColor,
                            }}
                          >
                            <Bot size={24} />
                          </Avatar>
                          <Box>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarUpload}
                              style={{ display: "none" }}
                              id="agent-avatar-upload"
                            />
                            <label htmlFor="agent-avatar-upload">
                              <LoadingButton
                                component="span"
                                variant="outlined"
                                loading={avatarUploadLoading}
                                sx={{ mb: 1, display: "block" }}
                              >
                                Upload Avatar
                              </LoadingButton>
                            </label>
                            {avatarImage && (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => setAvatarImage("")}
                              >
                                Remove
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <TextField
                        label="Primary Color"
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        fullWidth
                        InputProps={{
                          style: { height: 56 },
                        }}
                        sx={{
                          '& input[type="color"]': {
                            borderRadius: "8px",
                            "@media (max-width: 600px)": {
                              borderRadius: "4px",
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <TextField
                        label="Secondary Color"
                        type="color"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        fullWidth
                        InputProps={{
                          style: { height: 56 },
                        }}
                        sx={{
                          '& input[type="color"]': {
                            borderRadius: "8px",
                            "@media (max-width: 600px)": {
                              borderRadius: "4px",
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {employeeText} Persona (Optional)
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          {selectedEmployee ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                src={selectedEmployee.picture}
                                sx={{ width: 32, height: 32 }}
                              />
                              <Typography>{selectedEmployee.name}</Typography>
                            </Box>
                          ) : (
                            <Typography color="text.secondary">
                              No {employeeLower} selected
                            </Typography>
                          )}
                          <Button
                            variant="outlined"
                            onClick={() => setEmployeeSelectOpen(true)}
                            startIcon={<ContentCopyOutlined />}
                          >
                            {selectedEmployee ? "Change" : "Select"}
                          </Button>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          System Prompt Template
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel>Template</InputLabel>
                          <Select
                            label="Template"
                            value={systemPromptTemplate}
                            onChange={(e) =>
                              handleTemplateChange(e.target.value)
                            }
                          >
                            {systemPromptTemplates.map((template) => (
                              <MenuItem key={template.id} value={template.id}>
                                {template.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <PopOutTextInput
                        label="System Prompt"
                        value={systemPrompt}
                        onChange={setSystemPrompt}
                        multiline
                        rows={4}
                        fullWidth
                        placeholder="Enter the system prompt that defines the agent's role and behavior..."
                        dialogTitle="Edit System Prompt"
                        dialogDescription="Define the agent's role, behavior, and instructions. This prompt will guide how the agent responds to users."
                        popOutRows={25}
                        aiContext="This is a system prompt for an AI chatbot/agent. The prompt should define the agent's role, personality, behavior guidelines, and instructions for how to respond to users. Format it as clear instructions to an AI assistant."
                        getToken={getToken as () => Promise<string>}
                        companyId={companyId}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={knowledgeBaseEnabled}
                              onChange={(e) =>
                                setKnowledgeBaseEnabled(e.target.checked)
                              }
                            />
                          }
                          label="Enable Knowledge Base (+400ms latency)"
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          When enabled, the agent will use your knowledge base
                          documents to answer questions. You can optionally
                          scope which documents the agent has access to.
                        </Typography>
                      </Box>
                      {knowledgeBaseEnabled && (
                        <KnowledgeBaseSelect
                          getToken={getToken}
                          eventListener={eventListener}
                          company={company}
                          value={knowledgeBaseScope}
                          onValueChange={setKnowledgeBaseScope}
                          title="Knowledge Base Scope (Optional)"
                          sx={{ mt: 0 }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">
                            Lead Information Collection
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              Select which information your agent should gather
                              from users.
                            </Typography>

                            <Grid container spacing={1}>
                              {availableFields.map((field) => (
                                <Grid item key={field.key}>
                                  <Chip
                                    label={field.label}
                                    variant={
                                      leadFields.some(
                                        (f) => f.key === field.key
                                      )
                                        ? "filled"
                                        : "outlined"
                                    }
                                    onClick={() =>
                                      handleLeadFieldToggle(field.key)
                                    }
                                    color={
                                      leadFields.some(
                                        (f) => f.key === field.key
                                      )
                                        ? "primary"
                                        : "default"
                                    }
                                    size="small"
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>

                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">
                            Smart List Integration
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              Automatically add new conversation contacts to a
                              smart list when they submit their information.
                            </Typography>

                            <SmartListPicker
                              companyId={companyId}
                              getToken={getToken}
                              value={contactListId}
                              onChange={(listId) => setContactListId(listId)}
                              label="Contact Collection List (Optional)"
                              helperText="Select a list to automatically add new contacts to when they submit their information"
                              showCount
                              emptyLabel="None - Don't collect contacts"
                            />
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>

                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">
                            CRM Sync Integration
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              Automatically sync contact information to your CRM
                              when collected from conversations.
                            </Typography>

                            <FormControlLabel
                              control={
                                <Switch
                                  checked={crmSyncEnabled}
                                  onChange={(e) => {
                                    setCrmSyncEnabled(e.target.checked);
                                    if (!e.target.checked) {
                                      setCrmIntegrationId("");
                                    }
                                  }}
                                />
                              }
                              label="Enable CRM Sync"
                              sx={{ mb: 2 }}
                            />

                            {crmSyncEnabled && (
                              <CrmIntegrationSelect
                                value={crmIntegrationId}
                                onChange={setCrmIntegrationId}
                                getToken={getToken as () => Promise<string>}
                                companyId={companyId}
                                helperText="Contacts will be created or updated in this CRM when information is collected"
                              />
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Agent Response Delay (seconds)"
                        type="number"
                        value={debounceTimeout}
                        onChange={(e) =>
                          setDebounceTimeout(parseFloat(e.target.value) || 0.0)
                        }
                        fullWidth
                        inputProps={{ min: 0, max: 10, step: 0.5 }}
                        helperText="How long to wait before responding when users send multiple messages quickly"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Integrations Tab */}
              {currentTab === 1 && (
                <Box>
                  <Grid container spacing={3}>
                    {/* Toolsets Section */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Toolsets
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Connect curated toolsets to provide your agent with
                          specific tools from your connected integrations.
                          Toolsets allow you to control exactly which tools
                          your agent can use.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<SettingsIcon />}
                          onClick={() => setToolsetsDialogOpen(true)}
                        >
                          Manage Toolsets
                        </Button>
                      </Box>
                      <ToolsetSelect
                        key={toolsetSelectKey}
                        getToken={getToken}
                        eventListener={eventListener}
                        companyId={companyId}
                        value={toolsets}
                        onValueChange={setToolsets}
                        title="Available Toolsets"
                        sx={{ mt: 0 }}
                      />
                    </Grid>

                    {/* MCP Servers Section */}
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          MCP (Model Context Protocol) Servers
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Connect external MCP servers to provide your agent
                          with additional tools and capabilities. MCP servers
                          can provide access to databases, APIs, file systems,
                          and other external resources.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <MCPServerSelect
                        getToken={getToken}
                        eventListener={eventListener}
                        companyId={companyId}
                        value={mcpServers}
                        onValueChange={setMcpServers}
                        title="Available MCP Servers"
                        sx={{ mt: 0 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: "warning.main",
                          color: "warning.contrastText",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          Security Warning
                        </Typography>
                        <Typography variant="body2">
                          Agents are publicly accessible and any user can
                          interact with them. Only connect toolsets and MCP
                          servers that provide <strong>read-only</strong> or
                          <strong> scoped</strong> access. Avoid giving agents
                          tools that can modify sensitive data, delete
                          resources, or access confidential information.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          mt: 1,
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          border: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          How Integrations Work
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Toolsets</strong> let you curate specific
                          tools from your connected integrations (like Gmail,
                          Slack, etc.) and assign them to agents. This gives
                          you fine-grained control over what each agent can do.
                          <br />
                          <br />
                          <strong>MCP Servers</strong> connect to external
                          services using the Model Context Protocol, providing
                          additional capabilities like database access, API
                          integrations, or custom tools.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Website Tab */}
              {currentTab === 2 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={channelSettings.website.enabled}
                              onChange={(e) =>
                                updateWebsiteSettings({
                                  enabled: e.target.checked,
                                })
                              }
                            />
                          }
                          label="Enable Website Chat Widget"
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Allow users to chat with your agent through a widget
                          on your website.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Position</InputLabel>
                        <Select
                          label="Position"
                          value={channelSettings.website.position}
                          onChange={(e) =>
                            updateWebsiteSettings({ position: e.target.value })
                          }
                          disabled={!channelSettings.website.enabled}
                        >
                          {positions.map((p) => (
                            <MenuItem key={p.value} value={p.value}>
                              {p.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Button Text"
                        value={channelSettings.website.buttonText}
                        onChange={(e) =>
                          updateWebsiteSettings({ buttonText: e.target.value })
                        }
                        fullWidth
                        disabled={!channelSettings.website.enabled}
                      />
                    </Grid>

                    {/* Widget Button Style */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Widget Button Style
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {/* Chat Icon Option */}
                        <Box
                          onClick={() =>
                            channelSettings.website.enabled &&
                            updateWebsiteSettings({ widgetStyle: "default" })
                          }
                          sx={{
                            flex: "1 1 150px",
                            maxWidth: 200,
                            p: 2,
                            border: 2,
                            borderColor:
                              (channelSettings.website.widgetStyle ||
                                "default") === "default"
                                ? "primary.main"
                                : "divider",
                            borderRadius: 2,
                            cursor: channelSettings.website.enabled
                              ? "pointer"
                              : "not-allowed",
                            opacity: channelSettings.website.enabled ? 1 : 0.5,
                            textAlign: "center",
                            transition: "all 0.2s",
                            "&:hover": channelSettings.website.enabled
                              ? {
                                  borderColor: "primary.main",
                                  bgcolor: "action.hover",
                                }
                              : {},
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              bgcolor: primaryColor,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              mb: 1,
                            }}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="white"
                              width="24"
                              height="24"
                            >
                              <path d="M20 3H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h4v3l4-3h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                            </svg>
                          </Box>
                          <Typography variant="body2" fontWeight={600}>
                            Chat Icon
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Standard chat bubble
                          </Typography>
                        </Box>

                        {/* Avatar Option */}
                        <Box
                          onClick={() =>
                            channelSettings.website.enabled &&
                            updateWebsiteSettings({ widgetStyle: "avatar" })
                          }
                          sx={{
                            flex: "1 1 150px",
                            maxWidth: 200,
                            p: 2,
                            border: 2,
                            borderColor:
                              channelSettings.website.widgetStyle === "avatar"
                                ? "primary.main"
                                : "divider",
                            borderRadius: 2,
                            cursor: channelSettings.website.enabled
                              ? "pointer"
                              : "not-allowed",
                            opacity: channelSettings.website.enabled ? 1 : 0.5,
                            textAlign: "center",
                            transition: "all 0.2s",
                            "&:hover": channelSettings.website.enabled
                              ? {
                                  borderColor: "primary.main",
                                  bgcolor: "action.hover",
                                }
                              : {},
                          }}
                        >
                          <Avatar
                            src={avatarImage}
                            sx={{
                              width: 48,
                              height: 48,
                              mx: "auto",
                              mb: 1,
                              bgcolor: primaryColor,
                              border: 2,
                              borderColor: "background.paper",
                              boxShadow: 1,
                            }}
                          >
                            <Bot size={24} />
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>
                            Agent Avatar
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Uses agent's avatar
                          </Typography>
                        </Box>

                        {/* GIF Option */}
                        <Box
                          onClick={() =>
                            channelSettings.website.enabled &&
                            updateWebsiteSettings({ widgetStyle: "gif" })
                          }
                          sx={{
                            flex: "1 1 150px",
                            maxWidth: 200,
                            p: 2,
                            border: 2,
                            borderColor:
                              channelSettings.website.widgetStyle === "gif"
                                ? "primary.main"
                                : "divider",
                            borderRadius: 2,
                            cursor: channelSettings.website.enabled
                              ? "pointer"
                              : "not-allowed",
                            opacity: channelSettings.website.enabled ? 1 : 0.5,
                            textAlign: "center",
                            transition: "all 0.2s",
                            "&:hover": channelSettings.website.enabled
                              ? {
                                  borderColor: "primary.main",
                                  bgcolor: "action.hover",
                                }
                              : {},
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              bgcolor: "grey.200",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              mb: 1,
                              overflow: "hidden",
                              border: 2,
                              borderColor: "background.paper",
                              boxShadow: 1,
                            }}
                          >
                            {channelSettings.website.widgetGifUrl ? (
                              channelSettings.website.widgetGifUrl.endsWith(
                                ".mp4"
                              ) ||
                              channelSettings.website.widgetGifUrl.includes(
                                "video"
                              ) ? (
                                <video
                                  src={channelSettings.website.widgetGifUrl}
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <img
                                  src={channelSettings.website.widgetGifUrl}
                                  alt="Widget GIF"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              )
                            ) : (
                              <Typography variant="caption">GIF</Typography>
                            )}
                          </Box>
                          <Typography variant="body2" fontWeight={600}>
                            Custom GIF
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Animated button
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Widget Size - only for avatar and gif styles */}
                    {(channelSettings.website.widgetStyle === "avatar" ||
                      channelSettings.website.widgetStyle === "gif") && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Widget Size
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {[
                            { value: "sm", label: "Small", size: 48 },
                            { value: "md", label: "Medium", size: 60 },
                            { value: "lg", label: "Large", size: 80 },
                          ].map((sizeOption) => (
                            <Box
                              key={sizeOption.value}
                              onClick={() =>
                                channelSettings.website.enabled &&
                                updateWebsiteSettings({
                                  widgetSize: sizeOption.value,
                                })
                              }
                              sx={{
                                p: 1.5,
                                border: 2,
                                borderColor:
                                  (channelSettings.website.widgetSize ||
                                    "md") === sizeOption.value
                                    ? "primary.main"
                                    : "divider",
                                borderRadius: 2,
                                cursor: channelSettings.website.enabled
                                  ? "pointer"
                                  : "not-allowed",
                                opacity: channelSettings.website.enabled
                                  ? 1
                                  : 0.5,
                                textAlign: "center",
                                transition: "all 0.2s",
                                minWidth: 80,
                                "&:hover": channelSettings.website.enabled
                                  ? {
                                      borderColor: "primary.main",
                                      bgcolor: "action.hover",
                                    }
                                  : {},
                              }}
                            >
                              <Box
                                sx={{
                                  width: sizeOption.size * 0.6,
                                  height: sizeOption.size * 0.6,
                                  borderRadius: "50%",
                                  bgcolor: primaryColor,
                                  mx: "auto",
                                  mb: 0.5,
                                }}
                              />
                              <Typography variant="caption" fontWeight={600}>
                                {sizeOption.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {sizeOption.size}px
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* GIF URL Input - shown when GIF style is selected */}
                    {channelSettings.website.widgetStyle === "gif" && (
                      <Grid item xs={12}>
                        {/* Generate from Avatar Button */}
                        <Box sx={{ mb: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={() => setGenerateGifDialogOpen(true)}
                            disabled={
                              !channelSettings.website.enabled || !avatarImage
                            }
                            sx={{ mr: 1 }}
                          >
                            Generate from Avatar (30 credits)
                          </Button>
                          {!avatarImage && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              Set an avatar image first to generate an animation
                            </Typography>
                          )}
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Or enter a custom URL:
                        </Typography>
                        <TextField
                          label="Animation URL"
                          value={channelSettings.website.widgetGifUrl || ""}
                          onChange={(e) =>
                            updateWebsiteSettings({
                              widgetGifUrl: e.target.value,
                            })
                          }
                          fullWidth
                          disabled={!channelSettings.website.enabled}
                          placeholder="https://example.com/your-animation.gif or .mp4"
                          helperText="Enter the URL of a GIF or video. For best results, use a square format (1:1 aspect ratio)."
                        />

                        {/* Loop Option */}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                channelSettings.website.widgetGifLoop !== false
                              }
                              onChange={(e) =>
                                updateWebsiteSettings({
                                  widgetGifLoop: e.target.checked,
                                })
                              }
                              disabled={!channelSettings.website.enabled}
                            />
                          }
                          label="Loop animation"
                          sx={{ mt: 1 }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", ml: 4 }}
                        >
                          {channelSettings.website.widgetGifLoop !== false
                            ? "Animation will repeat continuously"
                            : "Animation will play once when the page loads"}
                        </Typography>
                        {channelSettings.website.widgetGifUrl && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mb: 1, display: "block" }}
                            >
                              Preview:
                            </Typography>
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                overflow: "hidden",
                                boxShadow: 2,
                              }}
                            >
                              {channelSettings.website.widgetGifUrl.endsWith(
                                ".mp4"
                              ) ||
                              channelSettings.website.widgetGifUrl.includes(
                                "video"
                              ) ? (
                                <video
                                  src={channelSettings.website.widgetGifUrl}
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <img
                                  src={channelSettings.website.widgetGifUrl}
                                  alt="Animation Preview"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    (
                                      e.target as HTMLImageElement
                                    ).style.display = "none";
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <PopOutTextInput
                        label="Welcome Message"
                        value={channelSettings.website.welcomeMessage}
                        onChange={(value) =>
                          updateWebsiteSettings({
                            welcomeMessage: value,
                          })
                        }
                        multiline
                        rows={2}
                        fullWidth
                        disabled={!channelSettings.website.enabled}
                        dialogTitle="Edit Welcome Message"
                        dialogDescription="This message will be displayed when users first open the chat widget."
                        aiContext="This is a welcome message for a website chat widget. It should be friendly, brief, and invite the user to start a conversation. Keep it to 1-2 sentences."
                        getToken={getToken as () => Promise<string>}
                        companyId={companyId}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Input Placeholder"
                        value={channelSettings.website.placeholder}
                        onChange={(e) =>
                          updateWebsiteSettings({ placeholder: e.target.value })
                        }
                        fullWidth
                        disabled={!channelSettings.website.enabled}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Scheduling Button
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Add a scheduling link to show a button in the chat
                        widget (e.g., Calendly, Cal.com)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            label="Scheduling Link"
                            value={schedulingLink}
                            onChange={(e) => setSchedulingLink(e.target.value)}
                            fullWidth
                            disabled={!channelSettings.website.enabled}
                            placeholder="https://calendly.com/your-link"
                            InputProps={{
                              startAdornment: (
                                <CalendarMonthIcon
                                  sx={{ mr: 1, color: "text.secondary" }}
                                />
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Button Text"
                            value={schedulingLinkText || "Schedule"}
                            onChange={(e) => {
                              const value = e.target.value.slice(0, 20);
                              setSchedulingLinkText(
                                value === "Schedule" ? "" : value
                              );
                            }}
                            fullWidth
                            disabled={
                              !channelSettings.website.enabled || !schedulingLink
                            }
                            helperText="Max 20 characters"
                            inputProps={{ maxLength: 20 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              channelSettings.website.contactForm || false
                            }
                            onChange={(e) =>
                              updateWebsiteSettings({
                                contactForm: e.target.checked,
                              })
                            }
                            disabled={!channelSettings.website.enabled}
                          />
                        }
                        label="Enable Contact Form"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Show a contact form to collect user information before
                        starting the conversation. Configure which fields to
                        collect in the "Lead Information Collection" section
                        above.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Response Delivery
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              channelSettings.website?.streamingEnabled || false
                            }
                            onChange={(e) =>
                              updateWebsiteSettings({
                                streamingEnabled: e.target.checked,
                              })
                            }
                            disabled={!channelSettings.website.enabled}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            Enable Streaming Responses
                            <Chip label="Beta" size="small" color="primary" />
                          </Box>
                        }
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, mb: 1.5 }}
                      >
                        Stream AI responses word-by-word as they're generated.
                        When disabled, responses appear all at once after
                        completion.
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              channelSettings.website?.multiMessageEnabled ||
                              false
                            }
                            onChange={(e) =>
                              updateWebsiteSettings({
                                multiMessageEnabled: e.target.checked,
                              })
                            }
                            disabled={!channelSettings.website.enabled}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            Allow Multiple Messages
                            <Chip label="Beta" size="small" color="primary" />
                          </Box>
                        }
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Allow the AI to break up long responses into multiple
                        separate messages for a more natural conversation flow.
                      </Typography>
                      <Tooltip
                        title={
                          !channelSettings.voice?.enabled ||
                          !channelSettings.voice?.elevenLabsVoiceId
                            ? "Enable Voice Agent and select a voice in the Voice tab first"
                            : ""
                        }
                        placement="top"
                      >
                        <span>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  channelSettings.website?.voiceEnabled || false
                                }
                                onChange={(e) =>
                                  updateWebsiteSettings({
                                    voiceEnabled: e.target.checked,
                                  })
                                }
                                disabled={
                                  !channelSettings.website?.enabled ||
                                  !channelSettings.voice?.enabled ||
                                  !channelSettings.voice?.elevenLabsVoiceId
                                }
                              />
                            }
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                Enable Voice Conversation
                                <Chip label="Beta" size="small" color="primary" />
                              </Box>
                            }
                            sx={{ mt: 1.5 }}
                          />
                        </span>
                      </Tooltip>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Allow users to have voice conversations with your agent
                        via the chat widget. Shows a phone button for voice
                        calls.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Accordion variant="outlined" disableGutters sx={{ boxShadow: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">Advanced</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TextField
                            label="Custom Channel Instructions"
                            value={
                              channelSettings.website?.customInstructions || ""
                            }
                            onChange={(e) =>
                              updateWebsiteSettings({
                                customInstructions: e.target.value,
                              })
                            }
                            multiline
                            rows={4}
                            fullWidth
                            placeholder="Override the default website chat channel instructions. For example, specify response formatting or behavior rules."
                            helperText="When provided, these instructions will be used instead of the default website channel instructions."
                          />
                          <ChannelSchedule
                            schedule={channelSettings.website?.schedule}
                            onChange={(schedule) =>
                              updateWebsiteSettings({ schedule })
                            }
                            disabled={!channelSettings.website?.enabled}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Webpage Tab */}
              {currentTab === 3 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={channelSettings.webpage?.enabled ?? true}
                              onChange={(e) =>
                                updateWebpageSettings({
                                  enabled: e.target.checked,
                                })
                              }
                            />
                          }
                          label="Enable Agent Webpage"
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Allow users to access a standalone webpage to chat with your agent via a shareable link.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Welcome Message"
                        value={channelSettings.webpage?.welcomeMessage || ""}
                        onChange={(e) =>
                          updateWebpageSettings({ welcomeMessage: e.target.value })
                        }
                        fullWidth
                        disabled={!channelSettings.webpage?.enabled}
                        helperText="The greeting message shown when users first visit the page"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Input Placeholder"
                        value={channelSettings.webpage?.placeholder || ""}
                        onChange={(e) =>
                          updateWebpageSettings({ placeholder: e.target.value })
                        }
                        fullWidth
                        disabled={!channelSettings.webpage?.enabled}
                        helperText="Placeholder text shown in the message input"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Description (shown below agent name in header)
                      </Typography>
                      <Box
                        sx={{
                          opacity: channelSettings.webpage?.enabled ? 1 : 0.5,
                          pointerEvents: channelSettings.webpage?.enabled ? "auto" : "none",
                        }}
                      >
                        <EditorProvider>
                          <RswEditor
                            style={{ height: 200 }}
                            containerProps={{ style: { resize: "vertical" } }}
                            value={channelSettings.webpage?.description || ""}
                            onChange={(e) =>
                              updateWebpageSettings({ description: e.target.value })
                            }
                          />
                        </EditorProvider>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Add a description or instructions below the agent name. You can include links using the editor toolbar.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              channelSettings.webpage?.contactForm || false
                            }
                            onChange={(e) =>
                              updateWebpageSettings({
                                contactForm: e.target.checked,
                              })
                            }
                            disabled={!channelSettings.webpage?.enabled}
                          />
                        }
                        label="Enable Contact Form"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Show a contact form to collect user information before
                        starting the conversation. Configure which fields to
                        collect in the "Lead Information Collection" section
                        above.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Response Delivery
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              channelSettings.webpage?.streamingEnabled || false
                            }
                            onChange={(e) =>
                              updateWebpageSettings({
                                streamingEnabled: e.target.checked,
                              })
                            }
                            disabled={!channelSettings.webpage?.enabled}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            Enable Streaming Responses
                            <Chip label="Beta" size="small" color="primary" />
                          </Box>
                        }
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Stream AI responses word-by-word as they're generated.
                        When disabled, responses appear all at once after
                        completion.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Tooltip
                        title={
                          !channelSettings.voice?.enabled || !channelSettings.voice?.elevenLabsVoiceId
                            ? "Enable Voice Agent and select a voice in the Voice tab first"
                            : ""
                        }
                        placement="top"
                      >
                        <span>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  channelSettings.webpage?.voiceEnabled || false
                                }
                                onChange={(e) =>
                                  updateWebpageSettings({
                                    voiceEnabled: e.target.checked,
                                  })
                                }
                                disabled={
                                  !channelSettings.webpage?.enabled ||
                                  !channelSettings.voice?.enabled ||
                                  !channelSettings.voice?.elevenLabsVoiceId
                                }
                              />
                            }
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                Enable Voice Conversation
                                <Chip label="Beta" size="small" color="primary" />
                              </Box>
                            }
                          />
                        </span>
                      </Tooltip>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Allow users to have voice conversations with your agent
                        on the webpage. Uses the voice configured in the Voice
                        tab.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Accordion variant="outlined" disableGutters sx={{ boxShadow: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">Advanced</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TextField
                            label="Custom Channel Instructions"
                            value={
                              channelSettings.webpage?.customInstructions || ""
                            }
                            onChange={(e) =>
                              updateWebpageSettings({
                                customInstructions: e.target.value,
                              })
                            }
                            multiline
                            rows={4}
                            fullWidth
                            disabled={!channelSettings.webpage?.enabled}
                            placeholder="Override the default webpage channel instructions. For example, specify response formatting or behavior rules."
                            helperText="When provided, these instructions will be used instead of the default webpage channel instructions."
                          />
                          <ChannelSchedule
                            schedule={channelSettings.webpage?.schedule}
                            onChange={(schedule) =>
                              updateWebpageSettings({ schedule })
                            }
                            disabled={!channelSettings.webpage?.enabled}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* SMS Tab */}
              {currentTab === 4 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={channelSettings.sms?.enabled || false}
                              onChange={(e) =>
                                updateSmsSettings({ enabled: e.target.checked })
                              }
                            />
                          }
                          label="Enable SMS Agent"
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Enable SMS messaging capabilities using your Twilio
                          integration. Users can send text messages to your
                          agent and receive responses.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Twilio Integration</InputLabel>
                        <Select
                          value={channelSettings.sms?.integrationId || ""}
                          onChange={(e) => {
                            updateSmsSettings({
                              integrationId: e.target.value,
                            });
                            // Clear phone number when integration changes
                            if (channelSettings.sms?.phoneNumber) {
                              updateSmsSettings({ phoneNumber: "" });
                            }
                          }}
                          label="Twilio Integration"
                          disabled={
                            !channelSettings.sms?.enabled || twilioLoading
                          }
                        >
                          <MenuItem value="">
                            <em>Select a Twilio integration</em>
                          </MenuItem>
                          {twilioIntegrations.map((integration) => (
                            <MenuItem
                              key={integration.id}
                              value={integration.id}
                            >
                              {integration.instanceName || integration.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {twilioIntegrations.length === 0 && !twilioLoading && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            No Twilio integrations found. Set up a Twilio
                            integration to enable SMS messaging.
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {channelSettings.sms?.integrationId && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <FormControl fullWidth>
                            <InputLabel>Phone Number</InputLabel>
                            <Select
                              value={channelSettings.sms?.phoneNumber || ""}
                              onChange={(e) => {
                                const phoneNumber = e.target.value;
                                updateSmsSettings({ phoneNumber });
                                // Auto-configure webhook when phone number is selected
                                if (
                                  phoneNumber &&
                                  channelSettings.sms?.integrationId
                                ) {
                                  configureWebhook(
                                    phoneNumber,
                                    channelSettings.sms.integrationId
                                  );
                                }
                              }}
                              label="Phone Number"
                              disabled={
                                !channelSettings.sms?.enabled ||
                                phoneNumbersLoading ||
                                webhookConfiguring
                              }
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {phoneNumbers.map((phone) => {
                                const agentUsing = getAgentUsingPhoneNumber(
                                  phone.phoneNumber,
                                  "sms"
                                );
                                return (
                                  <MenuItem
                                    key={phone.sid}
                                    value={phone.phoneNumber}
                                    disabled={!!agentUsing}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                      }}
                                    >
                                      <span>
                                        {phone.friendlyName ||
                                          phone.phoneNumber}
                                        {phone.friendlyName &&
                                          phone.phoneNumber &&
                                          phone.friendlyName !==
                                            phone.phoneNumber && (
                                            <Typography
                                              component="span"
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ ml: 1 }}
                                            >
                                              ({phone.phoneNumber})
                                            </Typography>
                                          )}
                                      </span>
                                      {agentUsing && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ ml: 1 }}
                                        >
                                          Used by {agentUsing.name}
                                        </Typography>
                                      )}
                                      {!agentUsing &&
                                        phone.smsUrl &&
                                        phone.smsUrl.includes(
                                          "/webhooks/twilio/sms"
                                        ) && (
                                          <Chip
                                            size="small"
                                            label="✓ Configured"
                                            color="success"
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                          <IconButton
                            onClick={() =>
                              loadPhoneNumbers(
                                channelSettings.sms?.integrationId || ""
                              )
                            }
                            disabled={
                              phoneNumbersLoading ||
                              !channelSettings.sms?.integrationId
                            }
                            sx={{ mb: 0.5 }}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Box>
                        {phoneNumbersLoading && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: -1, mb: 1, display: "block" }}
                          >
                            Loading phone numbers...
                          </Typography>
                        )}
                        {webhookConfiguring && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ mt: -1, mb: 1, display: "block" }}
                          >
                            Configuring webhook for SMS routing...
                          </Typography>
                        )}
                        {phoneNumbers.length === 0 &&
                          !phoneNumbersLoading &&
                          channelSettings.sms?.integrationId && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: -1, mb: 1, display: "block" }}
                            >
                              No phone numbers found in this Twilio account.
                              Please add a phone number to your Twilio account
                              first.
                            </Typography>
                          )}
                        {channelSettings.sms?.phoneNumber && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: -1, mb: 1, display: "block" }}
                          >
                            Webhook URL:{" "}
                            {isLocal
                              ? twilioWebhookHostname
                              : window.location.origin}
                            /webhooks/twilio/sms
                          </Typography>
                        )}
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <PopOutTextInput
                        label="Welcome Message"
                        value={
                          channelSettings.sms?.welcomeMessage ||
                          "Hello! How can I help you today?"
                        }
                        onChange={(value) =>
                          updateSmsSettings({ welcomeMessage: value })
                        }
                        multiline
                        rows={2}
                        fullWidth
                        disabled={!channelSettings.sms?.enabled}
                        sx={{ mb: 2 }}
                        dialogTitle="Edit SMS Welcome Message"
                        dialogDescription="This message will be sent when users first text the agent."
                        aiContext="This is a welcome message for SMS. It should be friendly, brief, and fit within SMS character limits. Keep it conversational and under 160 characters if possible."
                        getToken={getToken as () => Promise<string>}
                        companyId={companyId}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={channelSettings.sms?.autoReply || false}
                            onChange={(e) =>
                              updateSmsSettings({ autoReply: e.target.checked })
                            }
                            disabled={!channelSettings.sms?.enabled}
                          />
                        }
                        label="Auto-reply to incoming messages"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        When enabled, the agent will automatically respond to
                        incoming text messages.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Accordion variant="outlined" disableGutters sx={{ boxShadow: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">Advanced</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TextField
                            label="Custom Channel Instructions"
                            value={
                              channelSettings.sms?.customInstructions || ""
                            }
                            onChange={(e) =>
                              updateSmsSettings({
                                customInstructions: e.target.value,
                              })
                            }
                            multiline
                            rows={4}
                            fullWidth
                            disabled={!channelSettings.sms?.enabled}
                            placeholder="Override the default SMS channel instructions. For example, specify response formatting or length constraints."
                            helperText="When provided, these instructions will be used instead of the default SMS channel instructions."
                          />
                          <ChannelSchedule
                            schedule={channelSettings.sms?.schedule}
                            onChange={(schedule) =>
                              updateSmsSettings({ schedule })
                            }
                            disabled={!channelSettings.sms?.enabled}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Voice Tab */}
              {currentTab === 5 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={channelSettings.voice?.enabled || false}
                              onChange={(e) => {
                                const enabled = e.target.checked;
                                updateVoiceSettings({
                                  enabled: enabled,
                                });
                                // Save voice configuration when enabling voice
                                if (enabled) {
                                  handleVoiceConfigSave(
                                    channelSettings.voice?.elevenLabsVoiceId
                                  );
                                }
                              }}
                            />
                          }
                          label="Enable Voice Agent"
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Allow customers to call this agent using a phone
                          number.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Twilio Integration</InputLabel>
                        <Select
                          value={channelSettings.voice?.integrationId || ""}
                          onChange={(e) => {
                            updateVoiceSettings({
                              integrationId: e.target.value,
                            });
                            // Clear phone number when integration changes
                            if (channelSettings.voice?.phoneNumber) {
                              updateVoiceSettings({ phoneNumber: "" });
                            }
                          }}
                          label="Twilio Integration"
                          disabled={
                            !channelSettings.voice?.enabled || twilioLoading
                          }
                        >
                          <MenuItem value="">
                            <em>Select a Twilio integration</em>
                          </MenuItem>
                          {twilioIntegrations.map((integration) => (
                            <MenuItem
                              key={integration.id}
                              value={integration.id}
                            >
                              {integration.instanceName || integration.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {twilioLoading && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            Loading integrations...
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {channelSettings.voice?.integrationId && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <FormControl fullWidth>
                            <InputLabel>Phone Number</InputLabel>
                            <Select
                              value={channelSettings.voice?.phoneNumber || ""}
                              onChange={(e) => {
                                const phoneNumber = e.target.value;
                                // Configure voice when selecting a phone number
                                updateVoiceSettings({
                                  phoneNumber,
                                });
                                // Auto-configure webhook when phone number is selected
                                if (
                                  phoneNumber &&
                                  channelSettings.voice?.integrationId
                                ) {
                                  configureVoiceWebhook(
                                    phoneNumber,
                                    channelSettings.voice.integrationId
                                  );
                                }
                              }}
                              label="Phone Number"
                              disabled={
                                !channelSettings.voice?.enabled ||
                                phoneNumbersLoading ||
                                webhookConfiguring
                              }
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {phoneNumbers.map((phone) => {
                                const agentUsing = getAgentUsingPhoneNumber(
                                  phone.phoneNumber,
                                  "voice"
                                );
                                return (
                                  <MenuItem
                                    key={phone.sid}
                                    value={phone.phoneNumber}
                                    disabled={!!agentUsing}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                      }}
                                    >
                                      <span>
                                        {phone.friendlyName ||
                                          phone.phoneNumber}
                                        {phone.friendlyName &&
                                          phone.phoneNumber &&
                                          phone.friendlyName !==
                                            phone.phoneNumber && (
                                            <Typography
                                              component="span"
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ ml: 1 }}
                                            >
                                              ({phone.phoneNumber})
                                            </Typography>
                                          )}
                                      </span>
                                      {agentUsing && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ ml: 1 }}
                                        >
                                          Used by {agentUsing.name}
                                        </Typography>
                                      )}
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                          <IconButton
                            onClick={() =>
                              loadPhoneNumbers(
                                channelSettings.voice?.integrationId || ""
                              )
                            }
                            disabled={
                              phoneNumbersLoading ||
                              !channelSettings.voice?.integrationId
                            }
                            sx={{ mb: 0.5 }}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Box>
                        {webhookConfiguring && (
                          <Typography
                            variant="caption"
                            color="info.main"
                            sx={{ mt: 1, display: "block" }}
                          >
                            Configuring webhook for voice routing...
                          </Typography>
                        )}
                        {phoneNumbers.length === 0 &&
                          !phoneNumbersLoading &&
                          channelSettings.voice?.integrationId && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 1, mb: 1, display: "block" }}
                            >
                              No phone numbers found in this Twilio account.
                              Please add a phone number to your Twilio account
                              first.
                            </Typography>
                          )}
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <PopOutTextInput
                        label="Welcome Message"
                        value={
                          channelSettings.voice?.welcomeMessage ||
                          "Hello! Thanks for calling. How can I help you today?"
                        }
                        onChange={(value) =>
                          updateVoiceSettings({ welcomeMessage: value })
                        }
                        multiline
                        rows={2}
                        fullWidth
                        disabled={!channelSettings.voice?.enabled}
                        sx={{ mb: 2 }}
                        dialogTitle="Edit Voice Welcome Message"
                        dialogDescription="This message will be spoken when users first call the agent."
                        aiContext="This is a welcome message for a phone call voice agent. It will be spoken aloud using text-to-speech. Keep it natural, conversational, and friendly. Avoid complex words that might be mispronounced."
                        getToken={getToken as () => Promise<string>}
                        companyId={companyId}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              channelSettings.voice?.collectContactInfo || false
                            }
                            onChange={(e) =>
                              updateVoiceSettings({
                                collectContactInfo: e.target.checked,
                              })
                            }
                            disabled={!channelSettings.voice?.enabled}
                          />
                        }
                        label="Collect Contact Information"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, mb: 2 }}
                      >
                        Ask callers for the information configured in the "Lead
                        Information Collection" section above before starting
                        the conversation. This information will be saved to the
                        configured contact list.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              channelSettings.voice?.allowSmsDuringCall || false
                            }
                            onChange={(e) =>
                              updateVoiceSettings({
                                allowSmsDuringCall: e.target.checked,
                              })
                            }
                            disabled={!channelSettings.voice?.enabled}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            Allow SMS During Voice Call
                            <Chip label="Beta" size="small" />
                          </Box>
                        }
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, mb: 2 }}
                      >
                        Enable the AI agent to send SMS messages during a voice
                        call. This is useful for sending booking links, documents,
                        or follow-up information while speaking with the caller.
                        The AI will intelligently decide when to send an SMS based
                        on the conversation context.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              channelSettings.voice?.callHandoffEnabled || false
                            }
                            onChange={(e) =>
                              updateVoiceSettings({
                                callHandoffEnabled: e.target.checked,
                              })
                            }
                            disabled={!channelSettings.voice?.enabled}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            Enable Call Transfer/Handoff
                            <Chip label="Beta" size="small" />
                          </Box>
                        }
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, mb: 2 }}
                      >
                        Allow the AI agent to transfer calls to a human when
                        needed. Configure handoff targets in the "Handoff" tab
                        to define who the agent can transfer calls to and when.
                        The agent will use the phone numbers configured for each
                        handoff target.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Autocomplete
                          fullWidth
                          disabled={
                            !channelSettings.voice?.enabled || voicesLoading
                          }
                          loading={voicesLoading}
                          options={elevenLabsVoices}
                          value={
                            elevenLabsVoices.find(
                              (v) =>
                                v.voiceId ===
                                (channelSettings.voice?.elevenLabsVoiceId ||
                                  "pNInz6obpgDQGcFmaJgB")
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            if (newValue) {
                              updateVoiceSettings({
                                elevenLabsVoiceId: newValue.voiceId,
                              });
                              handleVoiceConfigSave(newValue.voiceId);
                            }
                          }}
                          getOptionLabel={(option) => {
                            const labels = option.labels || {};
                            const gender = labels.gender || option.gender || "";
                            const accent = labels.accent || option.accent || "";
                            const age = labels.age || option.age || "";

                            let label = option.name;
                            const details = [];
                            if (gender) details.push(gender);
                            if (accent) details.push(accent);
                            if (age) details.push(age);
                            if (details.length > 0) {
                              label += ` (${details.join(", ")})`;
                            }
                            return label;
                          }}
                          filterOptions={(options, { inputValue }) => {
                            const searchLower = inputValue.toLowerCase();
                            return options.filter((voice) => {
                              const name = voice.name?.toLowerCase() || "";
                              const description =
                                voice.description?.toLowerCase() || "";
                              const labels = voice.labels || {};
                              const gender = (
                                labels.gender ||
                                voice.gender ||
                                ""
                              ).toLowerCase();
                              const accent = (
                                labels.accent ||
                                voice.accent ||
                                ""
                              ).toLowerCase();
                              const age = (
                                labels.age ||
                                voice.age ||
                                ""
                              ).toLowerCase();
                              const category = (
                                voice.category || ""
                              ).toLowerCase();

                              return (
                                name.includes(searchLower) ||
                                description.includes(searchLower) ||
                                gender.includes(searchLower) ||
                                accent.includes(searchLower) ||
                                age.includes(searchLower) ||
                                category.includes(searchLower)
                              );
                            });
                          }}
                          renderOption={(props, option) => {
                            const { key, ...otherProps } = props as any;
                            return (
                              <Box
                                component="li"
                                key={key}
                                {...otherProps}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2">
                                    {option.name}
                                    {(() => {
                                      const labels = option.labels || {};
                                      const details = [];
                                      if (labels.gender || option.gender)
                                        details.push(
                                          labels.gender || option.gender
                                        );
                                      if (labels.accent || option.accent)
                                        details.push(
                                          labels.accent || option.accent
                                        );
                                      if (details.length > 0) {
                                        return ` (${details.join(", ")})`;
                                      }
                                      return "";
                                    })()}
                                  </Typography>
                                  {option.description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block" }}
                                    >
                                      {option.description.length > 80
                                        ? option.description.substring(0, 80) +
                                          "..."
                                        : option.description}
                                    </Typography>
                                  )}
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleVoicePreview(
                                      option.voiceId,
                                      option.name,
                                      e
                                    )
                                  }
                                  disabled={
                                    voicePreviewLoading === option.voiceId
                                  }
                                  sx={{ ml: 1 }}
                                >
                                  {voicePreviewLoading === option.voiceId ? (
                                    <RefreshIcon
                                      sx={{
                                        animation: "spin 1s linear infinite",
                                      }}
                                    />
                                  ) : playingVoiceId === option.voiceId ? (
                                    <StopIcon />
                                  ) : (
                                    <VolumeUpIcon />
                                  )}
                                </IconButton>
                              </Box>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Voice Selection"
                              placeholder="Search voices by name, accent, gender, or description..."
                              helperText={
                                voicesLoading
                                  ? "Loading voices..."
                                  : `Choose from ${elevenLabsVoices.length} high-quality voices powered by ElevenLabs`
                              }
                            />
                          )}
                          sx={{ mb: 2 }}
                        />
                        {/* Test button for selected voice */}
                        {channelSettings.voice?.elevenLabsVoiceId && (
                          <Button
                            variant="outlined"
                            size="large"
                            onClick={() => {
                              const selectedVoice = elevenLabsVoices.find(
                                (v) =>
                                  v.voiceId ===
                                  channelSettings.voice?.elevenLabsVoiceId
                              );
                              if (selectedVoice) {
                                handleVoicePreview(
                                  selectedVoice.voiceId,
                                  selectedVoice.name
                                );
                              }
                            }}
                            disabled={
                              !channelSettings.voice?.enabled ||
                              voicesLoading ||
                              voicePreviewLoading ===
                                channelSettings.voice?.elevenLabsVoiceId
                            }
                            startIcon={
                              voicePreviewLoading ===
                              channelSettings.voice?.elevenLabsVoiceId ? (
                                <RefreshIcon
                                  sx={{ animation: "spin 1s linear infinite" }}
                                />
                              ) : playingVoiceId ===
                                channelSettings.voice?.elevenLabsVoiceId ? (
                                <StopIcon />
                              ) : (
                                <PlayArrowIcon />
                              )
                            }
                            sx={{ minWidth: 120, height: 56 }}
                          >
                            {voicePreviewLoading ===
                            channelSettings.voice?.elevenLabsVoiceId
                              ? "Loading..."
                              : playingVoiceId ===
                                channelSettings.voice?.elevenLabsVoiceId
                              ? "Stop"
                              : "Test"}
                          </Button>
                        )}
                      </Box>
                    </Grid>

                    {/* ElevenLabs TTS Model Selection */}
                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={!channelSettings.voice?.enabled}>
                        <InputLabel>Text-to-Speech Model</InputLabel>
                        <Select
                          label="Text-to-Speech Model"
                          value={channelSettings.voice?.ttsModel || "eleven_flash_v2_5"}
                          onChange={(e) =>
                            updateVoiceSettings({ ttsModel: e.target.value })
                          }
                        >
                          <MenuItem value="eleven_flash_v2_5">
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                              <Typography variant="body2" fontWeight={600}>
                                Flash v2.5 (~75ms)
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Ultra-fast, optimized for real-time — 32 languages
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="eleven_turbo_v2_5">
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                              <Typography variant="body2" fontWeight={600}>
                                Turbo v2.5 (~250ms)
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                High quality, low latency balance — 32 languages
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="eleven_multilingual_v2">
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                              <Typography variant="body2" fontWeight={600}>
                                Multilingual v2
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Most lifelike with rich emotional expression — 29 languages
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="eleven_v3">
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                              <Typography variant="body2" fontWeight={600}>
                                Eleven v3 (Newest)
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Human-like and expressive — 70+ languages
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info">
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Recommended Models for Voice (Low Latency)
                        </Typography>
                        <Typography variant="body2" component="div">
                          For the best voice experience, choose a model optimized for fast response times (TTFT = Time to First Token):
                          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                            <li><strong>Llama 3.3 70B</strong> — Avg: 232ms, Min: 200ms TTFT</li>
                            <li><strong>GPT-4.1 Mini</strong> — Avg: 233ms, Min: 208ms TTFT</li>
                            <li><strong>GPT-4.1 Nano</strong> — Avg: 262ms, Min: 242ms TTFT</li>
                            <li><strong>Gemini 2.5 Flash</strong> — Avg: 451ms, Min: 407ms TTFT</li>
                          </Box>
                          <Box sx={{ mt: 1, color: "text.secondary" }}>
                            Configure the model in the Settings tab. Avoid reasoning models (o1, o3) for voice as they have higher latency (~3-5 seconds).
                          </Box>
                        </Typography>
                      </Alert>
                    </Grid>

                    <Grid item xs={12}>
                      <Accordion variant="outlined" disableGutters sx={{ boxShadow: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">Advanced</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TextField
                            label="Custom Channel Instructions"
                            value={
                              channelSettings.voice?.customInstructions || ""
                            }
                            onChange={(e) =>
                              updateVoiceSettings({
                                customInstructions: e.target.value,
                              })
                            }
                            multiline
                            rows={4}
                            fullWidth
                            disabled={!channelSettings.voice?.enabled}
                            placeholder="Override the default voice channel instructions. For example, specify speech patterns, call flow behavior, or TTS formatting rules."
                            helperText="When provided, these instructions will be used instead of the default voice channel instructions."
                            sx={{ mb: 2 }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{ mt: 2, mb: 1 }}
                          >
                            Idle Call Handling
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            End calls automatically when the caller goes silent
                            for too long. The agent will check for activity and
                            end the call after the specified number of idle
                            checks.
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            <TextField
                              label="Idle Time (seconds)"
                              type="number"
                              value={
                                channelSettings.voice?.idleTimeSeconds ?? 10
                              }
                              onChange={(e) =>
                                updateVoiceSettings({
                                  idleTimeSeconds: parseInt(e.target.value) || 10,
                                })
                              }
                              disabled={!channelSettings.voice?.enabled}
                              inputProps={{ min: 5, max: 60 }}
                              helperText="Seconds to wait before checking if caller is still there"
                              sx={{ flex: 1 }}
                            />
                            <TextField
                              label="Max Idle Checks"
                              type="number"
                              value={
                                channelSettings.voice?.maxIdleCheckCount ?? 3
                              }
                              onChange={(e) =>
                                updateVoiceSettings({
                                  maxIdleCheckCount: parseInt(e.target.value) || 3,
                                })
                              }
                              disabled={!channelSettings.voice?.enabled}
                              inputProps={{ min: 1, max: 10 }}
                              helperText="Number of checks before ending call"
                              sx={{ flex: 1 }}
                            />
                          </Box>
                          <ChannelSchedule
                            schedule={channelSettings.voice?.schedule}
                            onChange={(schedule) =>
                              updateVoiceSettings({ schedule })
                            }
                            disabled={!channelSettings.voice?.enabled}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Email Tab */}
              {currentTab === 6 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Email Agent Settings
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        Configure your agent to send and receive emails. Your
                        agent can automatically respond to incoming emails and
                        provide customer support via email.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={channelSettings.email?.enabled || false}
                            onChange={(e) =>
                              updateEmailSettings({ enabled: e.target.checked })
                            }
                          />
                        }
                        label="Enable Email Agent"
                      />
                    </Grid>

                    {channelSettings.email?.enabled && (
                      <>
                        <Grid item xs={12}>
                          <SearchableSenderProfileSelect
                            profiles={senderProfiles}
                            selectedId={
                              channelSettings.email?.senderProfileId || ""
                            }
                            onChangeSingle={(id) =>
                              updateEmailSettings({
                                senderProfileId: id,
                              })
                            }
                            label="Email Sender Profile"
                            disabled={!channelSettings.email?.enabled}
                            loading={senderProfilesLoading}
                            helperText={
                              senderProfiles.length === 0 &&
                              !senderProfilesLoading
                                ? "No sender profiles found. Create one to use email."
                                : "Select a sender profile for outgoing emails."
                            }
                            onManageProfiles={() =>
                              setSenderProfilesDialogOpen(true)
                            }
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Email Address (Receive)"
                            value={channelSettings.email?.emailAddress || ""}
                            onChange={(e) =>
                              updateEmailSettings({
                                emailAddress: e.target.value,
                              })
                            }
                            type="email"
                            fullWidth
                            disabled={true}
                            sx={{ mb: 2 }}
                            helperText="Email address is automatically set from the selected sender profile."
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <SearchableIntegrationSelect
                            integrations={emailIntegrations}
                            selectedId={channelSettings.email?.integrationId || ""}
                            onChangeSingle={(id) =>
                              updateEmailSettings({
                                integrationId: id,
                              })
                            }
                            label="Email Integration (Send)"
                            disabled={
                              !channelSettings.email?.enabled || emailLoading
                            }
                            loading={emailLoading}
                            helperText={
                              emailIntegrations.length === 0 && !emailLoading
                                ? "No email integrations found. Please configure an email integration (AWS SES or SendGrid) on the Integrations page first."
                                : "Select an email integration to send responses."
                            }
                            onManageIntegrations={() =>
                              window.open(
                                `/dashboard/companies/${companyId}/integrations`,
                                "_blank"
                              )
                            }
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  channelSettings.email?.autoReply || false
                                }
                                onChange={(e) =>
                                  updateEmailSettings({
                                    autoReply: e.target.checked,
                                  })
                                }
                                disabled={!channelSettings.email?.enabled}
                              />
                            }
                            label="Auto-reply to incoming emails"
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            When enabled, your agent will automatically respond
                            to incoming emails. When disabled, email responses
                            will be saved as drafts in your inbox for manual
                            review and approval. You can find and manage these
                            draft emails in the Inbox section, where they'll
                            appear with a "draft" status and can be reviewed,
                            edited, and sent when ready.
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  channelSettings.email?.humanOnly || false
                                }
                                onChange={(e) =>
                                  updateEmailSettings({
                                    humanOnly: e.target.checked,
                                  })
                                }
                                disabled={!channelSettings.email?.enabled}
                              />
                            }
                            label="Only respond to human emails"
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            When enabled, your agent will only respond to emails
                            from humans. Marketing emails, automated
                            notifications, and system messages will be ignored.
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  channelSettings.email?.saveDraftsToMailbox ||
                                  false
                                }
                                onChange={(e) =>
                                  updateEmailSettings({
                                    saveDraftsToMailbox: e.target.checked,
                                  })
                                }
                                disabled={
                                  !channelSettings.email?.enabled ||
                                  channelSettings.email?.autoReply !== false
                                }
                              />
                            }
                            label="Save drafts to mailbox draft folder"
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            When enabled and auto-reply is disabled, draft
                            emails will also be saved to your email client's
                            draft folder (e.g., Gmail drafts) for easy access.
                            This only works with IMAP email integrations.
                            {channelSettings.email?.autoReply !== false && (
                              <span style={{ color: "orange" }}>
                                {" "}
                                (Only available when auto-reply is disabled)
                              </span>
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Accordion variant="outlined" disableGutters sx={{ boxShadow: "none" }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1">
                                Advanced
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <TextField
                                label="Custom Channel Instructions"
                                value={
                                  channelSettings.email?.customInstructions ||
                                  ""
                                }
                                onChange={(e) =>
                                  updateEmailSettings({
                                    customInstructions: e.target.value,
                                  })
                                }
                                multiline
                                rows={4}
                                fullWidth
                                placeholder="Override the default email channel instructions. For example, specify how the agent should format responses, handle signatures, or structure email replies."
                                helperText="When provided, these instructions will be used instead of the default email channel instructions."
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      channelSettings.email
                                        ?.allowAiEmailHeaders || false
                                    }
                                    onChange={(e) =>
                                      updateEmailSettings({
                                        allowAiEmailHeaders: e.target.checked,
                                      })
                                    }
                                    disabled={
                                      !channelSettings.email?.enabled
                                    }
                                    size="small"
                                  />
                                }
                                label="Allow AI to set To, CC, BCC, and Reply-To fields (experimental)"
                                sx={{ mt: 2 }}
                              />
                              {channelSettings.email?.allowAiEmailHeaders && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ ml: 4, mt: 0.5 }}
                                >
                                  You must add instructions in the system prompt
                                  or custom channel instructions above to tell
                                  the AI when and how to set these fields.
                                </Typography>
                              )}
                              <ChannelSchedule
                                schedule={channelSettings.email?.schedule}
                                onChange={(schedule) =>
                                  updateEmailSettings({ schedule })
                                }
                                disabled={!channelSettings.email?.enabled}
                              />
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Handoff Tab */}
              {currentTab === 7 && (
                <Box>
                  <HandoffSection
                    handoffTargets={handoffTargets}
                    setHandoffTargets={setHandoffTargets}
                    disabled={saveLoading}
                  />
                </Box>
              )}

              {/* Notifications Tab */}
              {currentTab === 8 && (
                <Box>
                  <AgentNotificationSettings
                    settings={notificationSettings}
                    onChange={setNotificationSettings}
                    disabled={saveLoading}
                    companyId={companyId}
                    getToken={getToken as () => Promise<string>}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={onSave}
            loading={saveLoading}
            disabled={!agentName}
          >
            {editingAgent ? "Update" : "Create"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ToolsetsManagementDialog
        open={toolsetsDialogOpen}
        onClose={() => {
          setToolsetsDialogOpen(false);
          // Refresh the toolset select to show any newly created toolsets
          setToolsetSelectKey((prev) => prev + 1);
        }}
        companyId={companyId}
        getToken={getToken as () => Promise<string>}
        eventListener={eventListener}
      />

      <GenerateWidgetGifDialog
        open={generateGifDialogOpen}
        onClose={() => setGenerateGifDialogOpen(false)}
        agentId={editingAgent?.id || ""}
        companyId={companyId}
        avatarImage={avatarImage}
        getToken={getToken as () => Promise<string>}
        onGenerated={(videoUrl) => {
          updateWebsiteSettings({ widgetGifUrl: videoUrl });
        }}
      />

      <SenderProfiles
        open={senderProfilesDialogOpen}
        onClose={() => {
          setSenderProfilesDialogOpen(false);
          // Refresh sender profiles after dialog closes
          getToken().then(async (token: string) => {
            try {
              const allProfiles = await loadAllSenderProfiles(token, companyId);
              setSenderProfiles(allProfiles);
            } catch (err) {
              console.error("Failed to refresh sender profiles:", err);
            }
          });
        }}
        companyId={companyId}
        eventListener={eventListener}
        getToken={getToken}
        employees={employees}
        company={company}
      />
    </>
  );
};

export default AgentFormDialog;
